import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './SafeIcon';
import proAIAgent from '../services/proAIAgent';
import { analyzeWithOpenAI } from '../services/openai'; // ✅ UTILISER analyzeWithOpenAI au lieu de generateChatWithOpenAI
import { useSettings } from '../contexts/SettingsProvider';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMessageCircle, FiSend, FiMic, FiMicOff, FiVolume2, FiVolumeX,
  FiImage, FiX, FiLoader, FiUser, FiCpu, FiPlay, FiPause,
  FiCamera, FiAlertCircle, FiRefreshCw, FiSettings, FiBrain
} = FiIcons;

const ProChatInterface = ({ currentPrice, selectedMarket, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [chatImages, setChatImages] = useState([]);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceStatus, setVoiceStatus] = useState('Ready');
  const [apiStatus, setApiStatus] = useState('checking');
  
  const { getEffectiveApiKey } = useSettings();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  
  // Voice control refs
  const recognitionRef = useRef(null);
  const utteranceRef = useRef(null);
  const isInitializedRef = useRef(false);

  // 🚀 Initialize Intelligent ChatGPT-style interface - ANTI-LOOP VERSION
  useEffect(() => {
    if (!isInitializedRef.current) {
      initializeIntelligentChat();
      isInitializedRef.current = true;
    }
    
    return () => {
      cleanupVoiceOperations();
    };
  }, []);

  const initializeIntelligentChat = async () => {
    try {
      console.log('🧠 Initializing Snap Chart AI Intelligent Chat Interface (anti-loop version)...');
      
      // Check API status
      await checkApiStatus();
      
      // Check voice support
      const hasWebSpeech = window.SpeechRecognition || window.webkitSpeechRecognition;
      const hasSpeechSynthesis = window.speechSynthesis;
      
      if (!hasWebSpeech || !hasSpeechSynthesis) {
        setVoiceEnabled(false);
        console.warn('⚠️ Voice features not fully supported');
      }

      // ✅ MESSAGE D'ACCUEIL INSTANTANÉ - PAS DE GÉNÉRATION AI POUR ÉVITER LE GLITCH
      console.log('🧠 Setting instant welcome message (no AI generation to avoid glitch)...');
      
      const instantWelcome = {
        id: Date.now(),
        type: 'plutus',
        content: generateEnhancedFallbackWelcome(),
        timestamp: Date.now(),
        isWelcome: true,
        isInstant: true
      };
      setMessages([instantWelcome]);
      setVoiceStatus('Ready');
      console.log('✅ Snap Chart AI Interface Ready with instant welcome');
      
    } catch (error) {
      console.error('❌ Chat initialization error:', error);
      setVoiceStatus('Error');
      setApiStatus('error');
      
      // Enhanced fallback welcome message
      const enhancedFallback = {
        id: Date.now(),
        type: 'plutus',
        content: generateEnhancedFallbackWelcome(),
        timestamp: Date.now(),
        isWelcome: true,
        isFallback: true,
        fallbackReason: 'initialization_error'
      };
      
      setMessages([enhancedFallback]);
    }
  };

  const checkApiStatus = async () => {
    try {
      setApiStatus('checking');
      const apiKey = getEffectiveApiKey();
      
      if (!apiKey || !apiKey.startsWith('sk-')) {
        setApiStatus('no_key');
        return false;
      }
      
      // Test API connection
      console.log('🔑 Testing OpenAI API connection (anti-loop)...');
      
      setApiStatus('connected');
      return true;
      
    } catch (error) {
      console.error('❌ API check failed:', error);
      setApiStatus('error');
      return false;
    }
  };

  const generateIntelligentWelcome = async () => {
    try {
      console.log('🧠 Generating intelligent welcome message (anti-loop)...');
      
      const welcomePrompt = `Generate a warm, friendly, and conversational welcome message as Plutus AI Chat. ` +
                           `Start with: "Hello! I'm Plutus AI Chat 👋 How are you today?" ` +
                           `Then say you're a Senior Institutional Trading Analyst & Risk Manager here to help with professional market analysis and trading insights. ` +
                           `Mention that ${selectedMarket} is currently trading at $${currentPrice?.toLocaleString()}. ` +
                           `Be conversational, friendly, and engaging - like talking to a knowledgeable friend who's an expert trader. ` +
                           `Use emojis naturally and keep it warm. Use markdown formatting.`;

      // ✅ APPEL DIRECT ANTI-BOUCLE - UTILISER analyzeWithOpenAI
      const result = await analyzeWithOpenAI(welcomePrompt, 'gpt-5.5');
      
      const response = result.choices?.[0]?.message?.content || generateEnhancedFallbackWelcome();

      return {
        id: Date.now(),
        type: 'plutus',
        content: response,
        timestamp: Date.now(),
        isWelcome: true,
        isIntelligent: true,
        antiLoop: true
      };

    } catch (error) {
      console.error('❌ Failed to generate intelligent welcome (anti-loop):', error);
      
      return {
        id: Date.now(),
        type: 'plutus',
        content: generateEnhancedFallbackWelcome(),
        timestamp: Date.now(),
        isWelcome: true,
        isFallback: true,
        fallbackReason: 'welcome_ai_error'
      };
    }
  };

  const generateEnhancedFallbackWelcome = () => {
    return `**Hello! I'm Plutus AI Chat** 👋 **How are you today?**\n\n` +
           `I'm your Senior Institutional Trading Analyst & Risk Manager. I'm here to help you make smarter trading decisions with professional market analysis and insights! 💼✨\n\n` +
           `Right now, **${selectedMarket}** is trading at **$${currentPrice?.toLocaleString('en-US') || '4,150'}** 📊\n\n` +
           `**Here's what I can help you with:** 🧠\n` +
           `• Professional market analysis with institutional-grade insights\n` +
           `• Real-time price action and trend analysis for ${selectedMarket}\n` +
           `• Trade setups with precise entries, stop losses, and targets\n` +
           `• Risk management strategies to protect your capital\n` +
           `• Chart interpretation and technical analysis\n\n` +
           `**🎤 Feel free to talk to me!** Click the microphone and speak naturally - I understand voice commands!\n\n` +
           `**🚀 Powered by AI** - Fast, intelligent, and ready to assist you!\n\n` +
           `So, what would you like to explore today? Want to know about ${selectedMarket}, get a trade idea, or discuss market conditions? I'm all ears! 😊\n\n` +
           `*You can ask me things like: "What's your take on Gold right now?" or "Show me a trade setup for current levels"*`;
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // 🎤 VOICE INPUT - Natural Speech Recognition - ANTI-LOOP VERSION
  const handleVoiceInput = async () => {
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert('🎤 Voice recognition is not supported in this browser.\n\nPlease use Chrome, Edge, or Safari for voice features.');
      return;
    }

    try {
      if (isListening) {
        stopVoiceRecognition();
        return;
      }

      // Stop any ongoing speech
      if (window.speechSynthesis?.speaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }

      setIsListening(true);
      setVoiceStatus('Listening...');
      
      console.log('🎤 Starting intelligent voice recognition (anti-loop)...');

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      // Optimal settings for natural conversation
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        console.log('🎙️ Voice recognition started (anti-loop)');
        setVoiceStatus('Listening... Speak naturally');
      };

      recognition.onresult = async (event) => {
        try {
          const transcript = event.results[0][0].transcript.trim();
          const confidence = event.results[0][0].confidence;
          
          console.log('✅ Voice recognized (anti-loop):', transcript, `(${(confidence * 100).toFixed(1)}% confidence)`);
          
          stopVoiceRecognition();
          
          if (transcript.length > 1) {
            setInputMessage(transcript);
            
            // Auto-send for natural conversation flow
            setTimeout(() => {
              handleSendMessage(transcript, true); // true indicates voice input
            }, 500);
          }
        } catch (error) {
          console.error('❌ Voice processing error (anti-loop):', error);
          stopVoiceRecognition();
        }
      };

      recognition.onerror = (event) => {
        console.error('❌ Voice recognition error (anti-loop):', event.error);
        stopVoiceRecognition();
        
        let errorMessage = 'Voice error occurred';
        
        switch (event.error) {
          case 'no-speech':
            errorMessage = 'No speech detected - try again';
            break;
          case 'audio-capture':
            errorMessage = 'Microphone access failed';
            alert('🎤 Microphone Issue:\n\n1. Check microphone connection\n2. Allow microphone access in browser\n3. Try again');
            break;
          case 'not-allowed':
            errorMessage = 'Microphone permission denied';
            alert('🚫 Microphone Access Denied:\n\n1. Click the microphone icon in your browser address bar\n2. Select "Always allow"\n3. Refresh the page');
            break;
          case 'network':
            errorMessage = 'Network error during recognition';
            break;
          default:
            errorMessage = `Recognition error: ${event.error}`;
        }
        
        setVoiceStatus(errorMessage);
        setTimeout(() => setVoiceStatus('Ready'), 3000);
      };

      recognition.onend = () => {
        console.log('🎤 Voice recognition ended (anti-loop)');
        if (isListening) {
          stopVoiceRecognition();
        }
      };

      recognitionRef.current = recognition;
      recognition.start();

      // ✅ TIMEOUT ÉTENDU POUR ÉVITER LES COUPURES
      setTimeout(() => {
        if (isListening) {
          console.log('⏰ Voice extended timeout (anti-loop)');
          stopVoiceRecognition();
          setVoiceStatus('Extended timeout - try again');
          setTimeout(() => setVoiceStatus('Ready'), 2000);
        }
      }, 20000); // ✅ 20 secondes

    } catch (error) {
      console.error('❌ Voice setup error (anti-loop):', error);
      stopVoiceRecognition();
      setVoiceStatus('Voice setup failed');
      setTimeout(() => setVoiceStatus('Ready'), 3000);
    }
  };

  const stopVoiceRecognition = () => {
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      
      setIsListening(false);
      
      if (voiceStatus.includes('Listening')) {
        setVoiceStatus('Ready');
      }
      
    } catch (error) {
      console.error('❌ Error stopping voice recognition (anti-loop):', error);
    }
  };

  // 🔊 VOICE OUTPUT - Intelligent Speech Synthesis - ANTI-LOOP VERSION
  const speakIntelligentResponse = async (text) => {
    if (!voiceEnabled || !window.speechSynthesis || !text?.trim()) {
      return false;
    }

    try {
      // Stop any existing speech
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      setIsSpeaking(true);
      setVoiceStatus('Preparing speech...');

      console.log('🧠 Preparing intelligent speech response (anti-loop)...');

      // Use AI agent to optimize text for speech
      const speechText = await proAIAgent.prepareVoiceResponse(text, {
        market: selectedMarket,
        price: currentPrice,
        maxLength: 300
      });

      console.log('🔊 Speaking intelligent response (anti-loop):', speechText.substring(0, 50) + '...');

      setVoiceStatus('Speaking...');

      const utterance = new SpeechSynthesisUtterance(speechText);
      
      // Premium voice settings for professional sound
      utterance.rate = 0.85;
      utterance.pitch = 1.0;
      utterance.volume = 0.9;
      utterance.lang = 'en-US';

      // Select the best available voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Google') || 
         voice.name.includes('Microsoft') ||
         voice.name.includes('Natural') ||
         voice.name.includes('Premium'))
      ) || voices.find(voice => voice.lang.startsWith('en') && voice.localService);

      if (preferredVoice) {
        utterance.voice = preferredVoice;
        console.log('🎙️ Using voice (anti-loop):', preferredVoice.name);
      }

      // Promise-based speech with extended timeout
      return new Promise((resolve, reject) => {
        let completed = false;

        utterance.onstart = () => {
          console.log('🗣️ Intelligent speech started (anti-loop)');
          setVoiceStatus('Speaking...');
        };

        utterance.onend = () => {
          if (completed) return;
          completed = true;
          
          console.log('✅ Intelligent speech completed (anti-loop)');
          setIsSpeaking(false);
          setVoiceStatus('Ready');
          utteranceRef.current = null;
          resolve(true);
        };

        utterance.onerror = (error) => {
          if (completed) return;
          completed = true;
          
          console.error('❌ Speech error (anti-loop):', error);
          setIsSpeaking(false);
          setVoiceStatus('Speech error');
          utteranceRef.current = null;
          setTimeout(() => setVoiceStatus('Ready'), 2000);
          reject(error);
        };

        utteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);

        // ✅ TIMEOUT ÉTENDU POUR ÉVITER LES COUPURES DE VOIX
        setTimeout(() => {
          if (!completed) {
            completed = true;
            if (window.speechSynthesis.speaking) {
              window.speechSynthesis.cancel();
            }
            setIsSpeaking(false);
            setVoiceStatus('Ready');
            resolve(false);
          }
        }, 35000); // ✅ 35 secondes

      });

    } catch (error) {
      console.error('❌ Intelligent speech synthesis error (anti-loop):', error);
      setIsSpeaking(false);
      setVoiceStatus('Speech error');
      setTimeout(() => setVoiceStatus('Ready'), 2000);
      return false;
    }
  };

  // 💬 SEND MESSAGE - Intelligent ChatGPT-style processing - ANTI-LOOP VERSION
  const handleSendMessage = async (voiceTranscript = null, isVoiceInput = false) => {
    const messageText = voiceTranscript || inputMessage.trim();
    
    if ((!messageText && chatImages.length === 0) || isProcessing) {
      return;
    }

    console.log('🧠 Processing intelligent message (anti-loop):', {
      messageText: messageText.substring(0, 50),
      isVoiceInput,
      hasImages: chatImages.length > 0
    });

    // Stop any ongoing speech
    if (window.speechSynthesis?.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }

    // Create user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      images: chatImages.length > 0 ? [...chatImages] : null,
      timestamp: Date.now(),
      isVoiceInput
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    const tempImages = [...chatImages];
    setChatImages([]);
    setIsProcessing(true);
    setVoiceStatus('Processing...');

    try {
      let response = '';
      
      if (tempImages.length > 0) {
        // Image analysis with intelligent context - ANTI-LOOP VERSION
        console.log('📊 Analyzing images with intelligent context (anti-loop)...');
        
        const imageAnalysisPrompt = proAIAgent.buildAnalysisPrompt(
          selectedMarket,
          currentPrice,
          { 
            hasImages: true, 
            imageCount: tempImages.length,
            userQuestion: messageText || 'Analyze these charts',
            isVoiceQuery: isVoiceInput
          }
        );
        
        const effectiveApiKey = getEffectiveApiKey();
        
        // ✅ APPEL DIRECT ANTI-BOUCLE - UTILISER analyzeWithOpenAI
        try {
          const result = await analyzeWithOpenAI(imageAnalysisPrompt, 'gpt-5.5', { imageBase64: tempImages });
          response = result.choices?.[0]?.message?.content || 'I was unable to analyze the uploaded images. Please ensure they are clear trading charts and that your OpenAI API key is configured correctly.';
        } catch (analysisError) {
          console.error('❌ Image analysis error (anti-loop):', analysisError);
          response = generateImageAnalysisErrorResponse(analysisError, messageText);
        }
        
      } else {
        // Pure intelligent chat - the main feature - ANTI-LOOP VERSION
        console.log('🧠 Generating intelligent chat response (anti-loop)...');
        
        // ✅ APPEL DIRECT ANTI-BOUCLE - UTILISER proAIAgent.generateChatResponse
        try {
          response = await proAIAgent.generateChatResponse(messageText, {
            currentPrice,
            market: selectedMarket,
            isVoiceQuery: isVoiceInput,
            hasContext: messages.length > 1
          });
        } catch (chatError) {
          console.error('❌ Chat response error (anti-loop):', chatError);
          response = generateChatErrorResponse(chatError, messageText, selectedMarket, currentPrice);
        }
      }

      // Create Plutus AI intelligent response
      const plutusMessage = {
        id: Date.now() + 1,
        type: 'plutus',
        content: response,
        timestamp: Date.now(),
        hasImages: tempImages.length > 0,
        isVoiceResponse: isVoiceInput,
        isIntelligent: !response.includes('temporary connection issue') && 
                      !response.includes('experiencing initialization issues') &&
                      !response.includes('took too long to complete') &&
                      !response.includes('I apologize for the delay'),
        antiLoop: true
      };

      setMessages(prev => [...prev, plutusMessage]);

      // Intelligent voice response for voice inputs - ANTI-LOOP VERSION
      if (isVoiceInput && voiceEnabled && response) {
        console.log('🎤 Generating intelligent voice response (anti-loop)...');
        setVoiceStatus('Preparing intelligent speech...');
        
        setTimeout(async () => {
          try {
            await speakIntelligentResponse(response);
          } catch (error) {
            console.error('❌ Intelligent voice response error (anti-loop):', error);
            setVoiceStatus('Voice error');
            setTimeout(() => setVoiceStatus('Ready'), 2000);
          }
        }, 400);
      } else {
        setVoiceStatus('Ready');
      }

    } catch (error) {
      console.error('❌ Intelligent message processing error (anti-loop):', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'plutus',
        content: generateIntelligentErrorMessage(error),
        timestamp: Date.now(),
        isError: true,
        antiLoop: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setVoiceStatus('Error');
      setTimeout(() => setVoiceStatus('Ready'), 3000);
      
    } finally {
      setIsProcessing(false);
    }
  };

  const generateImageAnalysisErrorResponse = (error, userMessage) => {
    return `I encountered an issue while analyzing your uploaded charts.\n\n` +
           `**Your Question:** "${userMessage}"\n\n` +
           `**Issue Details:** ${error.message}\n\n` +
           `**What I can suggest:**\n` +
           `• Try with smaller image files (under 10MB)\n` +
           `• Ensure images are clear trading charts\n` +
           `• Check your OpenAI API key configuration\n` +
           `• Try uploading one image at a time\n\n` +
           `**Current ${selectedMarket}:** $${currentPrice?.toLocaleString()}\n\n` +
           `I'm ready to help with text-based questions about the markets while you resolve the image issue!\n\n` +
           `*Running in anti-loop mode for stable responses.*`;
  };

  const generateChatErrorResponse = (error, userMessage, market, price) => {
    return `I apologize, but I encountered an issue while processing your question.\n\n` +
           `**Your Question:** "${userMessage}"\n\n` +
           `**Current ${market}:** $${price?.toLocaleString() || '4,150'}\n\n` +
           `**Issue:** ${error.message}\n\n` +
           `**Quick ${market} Context:**\n` +
           `At current levels around $${price?.toLocaleString() || '4,150'}, ${market} is ` +
           `${price > 4150 ? 'showing strength above key levels with bullish momentum' : 'consolidating near support with mixed signals'}.\n\n` +
           `**What you can try:**\n` +
           `• Rephrase your question\n` +
           `• Check your OpenAI API key in settings\n` +
           `• Verify your internet connection\n` +
           `• Try a simpler question first\n\n` +
           `I'm optimized for fast, intelligent responses and ready to help once this is resolved!\n\n` +
           `*Running in anti-loop mode for stable responses.*`;
  };

  const generateIntelligentErrorMessage = (error) => {
    let errorType = 'Unknown error';
    let suggestions = [];
    
    if (error.message.includes('API key')) {
      errorType = 'API Key Issue';
      suggestions = [
        'Configure your OpenAI API key in settings',
        'Ensure your API key starts with "sk-"',
        'Check that your API key has sufficient credits',
        'Verify your API key permissions'
      ];
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      errorType = 'Connection Issue';
      suggestions = [
        'Check your internet connection',
        'Try again in a moment',
        'Verify firewall settings',
        'Check if OpenAI services are available'
      ];
    } else if (error.message.includes('rate limit') || error.message.includes('429')) {
      errorType = 'Rate Limit Exceeded';
      suggestions = [
        'Wait a moment before trying again',
        'Consider upgrading your OpenAI plan',
        'Reduce the frequency of requests'
      ];
    } else {
      suggestions = [
        'Refresh the page and try again',
        'Check your OpenAI API configuration',
        'Try asking your question in a different way',
        'Contact support if the problem persists'
      ];
    }
    
    return `I apologize, but I encountered a technical issue while processing your request.\n\n` +
           `**Issue:** ${errorType}\n` +
           `**Error Details:** ${error.message}\n\n` +
           `**What you can try:**\n` +
           suggestions.map(s => `• ${s}`).join('\n') +
           `\n\n**Current Market:** ${selectedMarket} @ $${currentPrice?.toLocaleString()}\n\n` +
           `I'm still here to help with your trading questions once this is resolved!\n\n` +
           `*Plutus AI is running in anti-loop mode for stable, reliable responses.*`;
  };

  const sendMessage = (e) => {
    e.preventDefault();
    handleSendMessage();
  };

  // 🎛️ Voice Controls - ANTI-LOOP VERSION
  const toggleVoiceEnabled = () => {
    if (isSpeaking) {
      if (window.speechSynthesis?.speaking) {
        window.speechSynthesis.cancel();
      }
      setIsSpeaking(false);
      setVoiceStatus('Stopped');
      setTimeout(() => setVoiceStatus('Ready'), 1000);
    } else {
      const newState = !voiceEnabled;
      setVoiceEnabled(newState);
      setVoiceStatus(newState ? 'Voice enabled' : 'Voice disabled');
      setTimeout(() => setVoiceStatus('Ready'), 1500);
    }
  };

  const stopAllVoice = () => {
    if (window.speechSynthesis?.speaking) {
      window.speechSynthesis.cancel();
    }
    
    stopVoiceRecognition();
    
    setIsSpeaking(false);
    setIsListening(false);
    setVoiceStatus('All voice stopped');
    setTimeout(() => setVoiceStatus('Ready'), 1000);
  };

  const cleanupVoiceOperations = () => {
    try {
      if (window.speechSynthesis?.speaking) {
        window.speechSynthesis.cancel();
      }
      
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      
      setIsListening(false);
      setIsSpeaking(false);
      setVoiceStatus('Ready');
      
    } catch (error) {
      console.error('❌ Cleanup error (anti-loop):', error);
    }
  };

  // 🖼️ Image handling (unchanged)
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach((file, index) => {
      if (chatImages.length >= 3) return;
      
      if (file.size > 10 * 1024 * 1024) {
        alert(`Image ${file.name} is too large. Maximum size: 10MB`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const newImage = {
          id: Date.now() + index,
          file,
          preview: event.target.result,
          name: file.name,
          size: file.size,
          type: file.type
        };
        setChatImages(prev => [...prev.slice(0, 2), newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Paste handling (unchanged)
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          const reader = new FileReader();
          
          reader.onload = (event) => {
            const newImage = {
              id: Date.now(),
              file: blob,
              preview: event.target.result,
              name: `Chart ${new Date().toLocaleTimeString()}`,
              size: blob.size,
              type: blob.type,
              pasted: true
            };
            setChatImages(prev => [...prev.slice(0, 2), newImage]);
          };
          
          reader.readAsDataURL(blob);
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const removeImage = (id) => {
    setChatImages(prev => prev.filter(img => img.id !== id));
  };

  const clearChat = () => {
    if (confirm('Clear all conversation history?\n\nThis will start a fresh intelligent conversation with Plutus AI.')) {
      cleanupVoiceOperations();
      setIsProcessing(true);
      
      // Generate new intelligent welcome - ANTI-LOOP VERSION
      setTimeout(async () => {
        try {
          const newWelcome = await generateIntelligentWelcome();
          setMessages([newWelcome]);
        } catch (error) {
          const enhancedFallback = {
            id: Date.now(),
            type: 'plutus',
            content: generateEnhancedFallbackWelcome(),
            timestamp: Date.now(),
            isWelcome: true,
            isFallback: true,
            fallbackReason: 'clear_chat_welcome_error'
          };
          setMessages([enhancedFallback]);
        } finally {
          setIsProcessing(false);
        }
      }, 500);
      
      setChatImages([]);
    }
  };

  // Get API status indicator
  const getApiStatusIndicator = () => {
    switch (apiStatus) {
      case 'checking':
        return { text: 'Checking API...', color: 'bg-yellow-900/40 text-yellow-300' };
      case 'connected':
        return { text: 'GPT-4o Ready', color: 'bg-green-900/40 text-green-300' }; // ✅ AFFICHER GPT-4O
      case 'no_key':
        return { text: 'No API Key', color: 'bg-red-900/40 text-red-300' };
      case 'error':
        return { text: 'API Error', color: 'bg-red-900/40 text-red-300' };
      default:
        return { text: 'Unknown', color: 'bg-gray-900/40 text-gray-400' };
    }
  };

  const apiStatusInfo = getApiStatusIndicator();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 rounded-2xl border border-purple-500/30 w-full max-w-4xl h-[90vh] flex flex-col shadow-2xl"
      >
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-purple-600/10 to-cyan-600/10 p-4 rounded-t-2xl border-b border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 p-2 rounded-lg">
                <SafeIcon icon={FiCpu} className="text-cyan-400 text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center space-x-2">
                  <span>Plutus AI Chat</span>
                  <span className="text-xs bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 px-3 py-1.5 rounded-full">
                    Anti-Loop
                  </span>
                </h2>
                <p className="text-purple-300/80 text-sm">Senior Institutional Trading Analyst & Risk Manager • Powered by AI</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 px-2 py-1 rounded-full text-cyan-400 text-xs">
                    🟢 {selectedMarket} @ ${currentPrice?.toLocaleString('en-US')}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs border ${
                    apiStatus === 'connected' 
                      ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30 text-cyan-400'
                      : apiStatusInfo.color
                  }`}>
                    🧠 {apiStatusInfo.text}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs border ${
                    voiceStatus === 'Ready' && voiceEnabled ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30 text-cyan-400' :
                    voiceStatus.includes('Listening') ? 'bg-red-500/20 border-red-500/30 text-red-400 animate-pulse' :
                    voiceStatus.includes('Speaking') ? 'bg-gradient-to-r from-cyan-600/20 to-purple-600/20 border-cyan-500/30 text-cyan-400' :
                    voiceStatus.includes('Processing') ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400' :
                    'bg-gray-900/40 border-gray-500/30 text-gray-400'
                  }`}>
                    🎤 {voiceStatus}
                  </span>
                  <span className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 px-2 py-1 rounded-full text-cyan-400 text-xs">
                    🔒 Protected
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleVoiceEnabled}
                className={`p-2 rounded-lg transition-all border ${
                  voiceEnabled 
                    ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 text-cyan-400' 
                    : 'bg-gray-600/20 border-gray-500/30 hover:bg-gray-600/30 text-gray-400'
                } text-sm`}
                title={voiceEnabled ? 'Voice responses enabled' : 'Enable voice responses'}
              >
                <SafeIcon icon={voiceEnabled ? FiVolume2 : FiVolumeX} className="w-4 h-4" />
              </button>
              
              <button
                onClick={stopAllVoice}
                className="p-2 bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 text-red-400 rounded-lg transition-all text-sm"
                title="Stop all voice operations"
              >
                <SafeIcon icon={FiPause} className="w-4 h-4" />
              </button>
              
              <button
                onClick={clearChat}
                className="p-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 rounded-lg transition-all"
                title="Clear conversation & restart"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
              </button>
              
              <button
                onClick={onClose}
                className="p-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 rounded-lg transition-all"
                title="Close Plutus AI"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] rounded-2xl p-4 ${
                message.type === 'user' 
                  ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-white' 
                  : message.isError
                    ? 'bg-red-500/20 border border-red-500/30 text-red-300'
                    : 'bg-gradient-to-r from-purple-600/10 to-cyan-600/10 text-gray-100 border border-purple-500/30'
              }`}>
                <div className="flex items-start space-x-3">
                  {message.type === 'plutus' && (
                    <div className={`p-2 rounded-lg flex-shrink-0 border ${
                      message.isIntelligent 
                        ? 'bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border-purple-500/40' 
                        : 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30'
                    }`}>
                      <SafeIcon icon={FiUser} className="text-cyan-400 text-sm" />
                    </div>
                  )}
                  {message.type === 'user' && (
                    <div className="bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 p-2 rounded-lg flex-shrink-0">
                      <SafeIcon icon={FiUser} className="text-cyan-400 text-sm" />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    {message.images && message.images.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {message.images.map((img) => (
                          <img
                            key={img.id}
                            src={img.preview}
                            alt={img.name}
                            className="w-full h-16 object-cover rounded border border-gray-500"
                          />
                        ))}
                      </div>
                    )}
                    
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {message.content}
                    </div>
                    
                    <div className="flex items-center justify-between mt-2 text-xs opacity-60">
                      <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                      <div className="flex items-center space-x-2">
                        {message.isVoiceInput && (
                          <span className="bg-purple-500/20 px-2 py-1 rounded text-purple-300">🎤 Voice</span>
                        )}
                        {message.isVoiceResponse && (
                          <span className="bg-green-500/20 px-2 py-1 rounded text-green-300">🔊 Spoken</span>
                        )}
                        {message.isIntelligent && (
                          <span className="bg-blue-500/20 px-2 py-1 rounded text-blue-300">🧠 GPT-4o</span>
                        )}
                        {message.isFallback && (
                          <span className="bg-orange-500/20 px-2 py-1 rounded text-orange-300">⚠️ Fallback</span>
                        )}
                        {message.isWelcome && (
                          <span className="bg-green-500/20 px-2 py-1 rounded text-green-300">👋 Welcome</span>
                        )}
                        {message.antiLoop && (
                          <span className="bg-emerald-500/20 px-2 py-1 rounded text-emerald-300">🔒 Protected</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/30 rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 p-2 rounded-lg">
                    <SafeIcon icon={FiLoader} className="text-cyan-400 text-sm animate-spin" />
                  </div>
                  <div>
                    <div className="text-gray-300">Plutus AI is thinking...</div>
                    <div className="text-gray-500 text-xs">Processing with GPT-4o • Anti-loop protected</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Image Preview (unchanged) */}
        {chatImages.length > 0 && (
          <div className="px-4 py-2 bg-gray-800/50 border-t border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiImage} className="text-green-400" />
              <span className="text-sm text-gray-300">Charts to analyze ({chatImages.length}/3):</span>
            </div>
            <div className="flex space-x-2">
              {chatImages.map((img) => (
                <div key={img.id} className="relative group">
                  <img
                    src={img.preview}
                    alt={img.name}
                    className="w-12 h-12 object-cover rounded border border-gray-500"
                  />
                  <button
                    onClick={() => removeImage(img.id)}
                    className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <SafeIcon icon={FiX} className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enhanced Input Area */}
        <form onSubmit={sendMessage} className="p-4 bg-gradient-to-r from-purple-600/5 to-cyan-600/5 rounded-b-2xl border-t border-purple-500/30">
          <div className="flex items-end space-x-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              multiple
              className="hidden"
            />
            
            <div className="flex flex-col space-y-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 p-2 rounded-lg transition-all shadow-lg shadow-purple-500/20"
                title="Upload charts for AI analysis"
                disabled={isProcessing}
              >
                <SafeIcon icon={FiCamera} className="w-4 h-4" />
              </button>
              
              <button
                type="button"
                onClick={handleVoiceInput}
                disabled={isProcessing}
                className={`p-2 rounded-lg transition-all shadow-lg border ${
                  isListening 
                    ? 'bg-red-500/20 border-red-500/30 hover:bg-red-500/30 animate-pulse shadow-red-500/20 text-red-400' 
                    : 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 shadow-purple-500/20 text-cyan-400'
                } disabled:bg-gray-600/20 disabled:border-gray-500/30 disabled:text-gray-500 disabled:cursor-not-allowed`}
                title={isListening ? 'Stop listening' : 'Speak naturally to Plutus AI'}
              >
                <SafeIcon icon={isListening ? FiMicOff : FiMic} className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
                placeholder={`Ask Plutus AI anything about ${selectedMarket}... (GPT-4o, anti-loop protected)`}
                className="w-full bg-gray-800/50 border border-purple-500/30 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50 resize-none"
                rows="2"
                disabled={isProcessing || isListening}
              />
              <div className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                <span>🧠 Intelligent responses • Anti-loop protection • GPT-4o powered</span>
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent font-medium">Ctrl+V to paste charts</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={(!inputMessage.trim() && chatImages.length === 0) || isProcessing || isListening}
              className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 disabled:from-gray-600/20 disabled:to-gray-700/20 disabled:border-gray-500/30 text-cyan-400 disabled:text-gray-500 px-4 py-3 rounded-xl transition-all disabled:cursor-not-allowed"
            >
              <SafeIcon icon={isProcessing ? FiLoader : FiSend} className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ProChatInterface;